import React from 'react';
import { bool, object } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { propTypes } from '../../util/types';
import { MARKETING_MAX_RETURN_VALUES } from '../../marketplace-custom-config';
import config from '../../config';
import {
  Page,
  SectionHero,
  SectionGotOrNeedATrailer,
  SectionForHirersSteps,
  SectionLocations,
  SectionTestimonialSlider,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
} from '../../components';
import { TopbarContainer } from '../../containers';

import facebookImage from '../../assets/lth-og.jpg';
import twitterImage from '../../assets/lth-twitter.jpg';
import {
  heroBannerAvif1280,
  heroBannerAvifSrcSet,
  heroBannerSizes,
} from '../../assets/heroBannerImages';

import css from './LandingPage.module.css';

export const LandingPageComponent = props => {
  const {
    history,
    intl,
    location,
    scrollingDisabled,
    currentUserListing = null,
    currentUserListingFetched = false,
  } = props;

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage(
    { id: 'LandingPage.schemaDescription' },
    { siteTitle }
  );
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  const learnMoreAboutHiringLink = (
    <NamedLink name="ForHirersPage" className={css.wideLink}>
      <span>
        <FormattedMessage id="LandingPage.learnMoreAboutHiring" />
      </span>
    </NamedLink>
  );

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 1024, height: 512 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <Helmet>
        {/*
          Preload only the preferred AVIF source. Preloading WebP as well would make
          AVIF-capable browsers download both formats. Keep srcset/sizes identical
          to the AVIF <source> in SectionHero.
        */}
        <link
          rel="preload"
          as="image"
          type="image/avif"
          href={heroBannerAvif1280}
          // Helmet/React: lowercase HTML attrs are the reliable form for link imagesrcset
          imagesrcset={heroBannerAvifSrcSet}
          imagesizes={heroBannerSizes}
          fetchpriority="high"
        />
      </Helmet>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="LandingPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.heroContainer}>
            <SectionHero className={css.hero} history={history} location={location} />
          </div>
          <div className={css.guarantee}>
            <NamedLink name="ForOwnersPage">
              <FormattedMessage id="SectionHero.guarantee" values={MARKETING_MAX_RETURN_VALUES} />
            </NamedLink>
          </div>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="LandingPage.benefits" />
                </h2>
                <div className={css.testimonial}>
                  <SectionTestimonialSlider />
                </div>
                <SectionForHirersSteps />
                <div className={css.learnMoreAboutHiring}>{learnMoreAboutHiringLink}</div>
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionGotOrNeedATrailer />
              </div>
            </li>

            <li className={css.section}>
              <div className={css.sectionContentFirstChild}>
                <SectionLocations />
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUserListing, currentUserListingFetched } = state.user;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUserListing,
    currentUserListingFetched,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(LandingPageComponent);

export default LandingPage;
